import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ColombiaService } from '../../services/colombia.service';
import { RailColombiaService } from '../../services/rail-colombia.service';
import {
  VitaPricesResponse,
  VitaWalletService,
} from '../../services/vita-wallet.service';
import {
  CalcConfig,
  CalcResultado,
  CALC_CONFIG_DEFAULT,
  calcOperacion,
  calcTabla,
} from './rail-colombia-calculadora.engine';

interface CobrebSnapshot {
  usdSell: number;
  fixedCostCOP: number;
  validUntil: string | null;
}

/**
 * Calculadora Aguacate COP (RAIL COLOMBIA) — modelo del xls con tasas reales.
 *
 * 100% frontend. Combina tasas en vivo de endpoints ya existentes:
 *   · Tasa PÚBLICA de Vita (referencia B3 del modelo) → `RailColombiaService.getLatestAll()`.
 *   · Investing (referencia de mercado, informativa)   → `RailColombiaService.getLatestAll()`.
 *   · usd_sell Vita COBREB (rate de compra B4)         → `VitaWalletService.getPrices()`.
 * Calcula client-side, con el markup C por monto (col C3..C13 del xls) tanto
 * en la tabla como en el simulador. No persiste nada.
 */
@Component({
  selector: 'app-rail-colombia-calculadora',
  templateUrl: './rail-colombia-calculadora.component.html',
  styleUrls: ['./rail-colombia-calculadora.component.sass'],
})
export class RailColombiaCalculadoraComponent implements OnInit, OnDestroy {
  // ── Inputs editables (se pre-cargan en vivo, el operador puede overridear) ──
  montoUSD = 100;
  /** B3 — tasa de referencia del modelo (tasa PÚBLICA de Vita). */
  trmVita = CALC_CONFIG_DEFAULT.trmVita;
  /** B4 — rate real de compra (usd_sell Vita COBREB). 0 = usar ×0.995. */
  trmSetup = 0;
  /** B20 — comisión % visible Aguacate. Prefill desde rail_config vigente. */
  aguacateFxPct = CALC_CONFIG_DEFAULT.aguacateFxPct;
  /** B21 — comisión fija USD Aguacate. Prefill desde rail_config vigente. */
  aguacateFijo = CALC_CONFIG_DEFAULT.aguacateFijo;
  /**
   * E — costo fijo total en COP = fee VIVO de Vita (`fixed_cost` API) + costo
   * interno extra de rail_config (hoy 0, WS1.3). Antes era 1500 hardcodeado:
   * coincidía de casualidad con el fee; si Vita lo cambia, mentía la ganancia.
   */
  costoFijoCOP = CALC_CONFIG_DEFAULT.costoFijoCOP;
  private feeVitaVivoCOP: number | null = null;
  private costoFijoCfgCOP: number | null = null;

  // ── Resultado ──
  resultado: CalcResultado = calcOperacion(this.montoUSD, this.buildCfg());
  tabla: CalcResultado[] = calcTabla(this.buildCfg());

  displayedColumns: string[] = [
    'usd',
    'markup',
    'copAguacate',
    'copVita',
    'comision',
    'ganancia',
  ];

  // ── Metadatos del prefill (informativos) ──
  /** B3 = tasa pública de Vita: fuente + fecha. */
  vitaPublicaFuente: string | null = null;
  vitaPublicaFecha: string | null = null;
  /** Referencia de mercado (Investing): valor + fuente + fecha. Informativa, NO entra al modelo. */
  investingRef: number | null = null;
  investingFuente: string | null = null;
  investingFecha: string | null = null;
  vitaFixedCostCOP = 0;
  vitaValidUntil: string | null = null;

  cargandoTrm = false;
  cargandoVita = false;
  cobrebDisponible: boolean | null = null;
  vitaError: string | null = null;

  private sub = new Subscription();

  constructor(
    private railService: RailColombiaService,
    private vita: VitaWalletService,
    private colombia: ColombiaService,
  ) {}

  ngOnInit(): void {
    this.prefillTasas();
    this.prefillVitaCobreb();
    this.prefillConfig();
  }

  /**
   * Prefill del markup desde la config VIGENTE del riel (rail_config, la misma
   * que edita el modal Configuración): comisión %, fijo USD y costo fijo
   * interno extra. Sin esto la calculadora quedaba con 1.69/0.81/1500
   * hardcodeados y divergía apenas ops guardara otra versión.
   */
  private async prefillConfig(): Promise<void> {
    try {
      const cfg = (await this.colombia.getConfig())?.COBREB;
      if (!cfg) return;
      // rail_config guarda PORCENTAJE (1.69); el motor usa FRACCIÓN (0.0169).
      const pct = Number(cfg.comisionPct);
      if (Number.isFinite(pct) && pct >= 0) this.aguacateFxPct = pct / 100;
      const fijo = Number(cfg.fijoUsd);
      if (Number.isFinite(fijo) && fijo >= 0) this.aguacateFijo = fijo;
      const extra = Number(cfg.costoFijoCop);
      this.costoFijoCfgCOP = Number.isFinite(extra) && extra >= 0 ? extra : 0;
      this.syncCostoFijo();
      this.recalcular();
    } catch {
      // Sin config: quedan los defaults del xls (el operador puede editar).
    }
  }

  /** Costo fijo total = fee vivo de Vita + costo interno extra de la config. */
  private syncCostoFijo(): void {
    if (this.feeVitaVivoCOP == null && this.costoFijoCfgCOP == null) return;
    this.costoFijoCOP =
      (this.feeVitaVivoCOP ?? 0) + (this.costoFijoCfgCOP ?? 0);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // ──────── Prefill B3 (tasa pública de Vita) + mercado (Investing, info) ────────

  async prefillTasas(): Promise<void> {
    this.cargandoTrm = true;
    try {
      const all = await this.railService.getLatestAll();

      // B3 del modelo = tasa PÚBLICA de Vita.
      const vitaP = all?.VITA_PUBLIC;
      const tasaVita =
        vitaP?.tasa != null ? parseFloat(String(vitaP.tasa)) : NaN;
      if (Number.isFinite(tasaVita) && tasaVita > 0) {
        this.trmVita = tasaVita;
        this.vitaPublicaFuente = this.fuenteLabel(vitaP.fuente);
        this.vitaPublicaFecha = vitaP.fechaHoraColombia ?? null;
      }

      // Referencia de mercado (Wise): informativa, NO entra al modelo —el
      // modelo usa B3 = tasa pública de Vita, arriba—. Antes leía INVESTING,
      // que no recibe datos desde el 2026-07-16 y mostraba siempre un guión.
      const inv = all?.WISE;
      const tasaInv = inv?.tasa != null ? parseFloat(String(inv.tasa)) : NaN;
      if (Number.isFinite(tasaInv) && tasaInv > 0) {
        this.investingRef = tasaInv;
        this.investingFuente = this.fuenteLabel(inv.fuente);
        this.investingFecha = inv.fechaHoraColombia ?? null;
      }

      this.recalcular();
    } catch {
      // Sin tasas: el operador puede teclear la referencia B3 a mano.
    } finally {
      this.cargandoTrm = false;
    }
  }

  // ─────────────── Prefill usd_sell Vita COBREB (B4, rate de compra) ───────────────

  prefillVitaCobreb(): void {
    this.cargandoVita = true;
    this.vitaError = null;
    this.sub.add(
      this.vita.getPrices().subscribe({
        next: (res) => {
          this.cargandoVita = false;
          const snap = this.extractCobreb(res);
          if (snap) {
            this.cobrebDisponible = true;
            this.trmSetup = snap.usdSell;
            this.vitaFixedCostCOP = snap.fixedCostCOP;
            this.vitaValidUntil = snap.validUntil;
            // El costo fijo del modelo es el fee VIVO de Vita (+ extra config),
            // no el 1500 histórico del xls.
            this.feeVitaVivoCOP = snap.fixedCostCOP;
            this.syncCostoFijo();
          } else {
            this.cobrebDisponible = false;
          }
          this.recalcular();
        },
        error: (err) => {
          this.cargandoVita = false;
          if (err?.transient) return;
          this.vitaError =
            typeof err === 'string' ? err : 'No se pudo obtener la tasa Vita';
        },
      }),
    );
  }

  /**
   * Extrae usd_sell + fixed_cost del tramo COBREB del response de /prices.
   * Mismo patrón que `extractPartner` del backend (`colombia-rail.service.ts`).
   */
  private extractCobreb(res: VitaPricesResponse): CobrebSnapshot | null {
    const fromBlock = (block: any): CobrebSnapshot | null => {
      const attrs = block?.prices?.attributes;
      const usdSell = attrs?.usd_sell;
      if (!usdSell || typeof usdSell !== 'object') return null;
      const key = Object.keys(usdSell).find((k) => /cobreb/i.test(k));
      if (!key) return null;
      const price = Number(usdSell[key]);
      if (!Number.isFinite(price) || price <= 0) return null;
      const fixed = Number((attrs.fixed_cost || {})[key]) || 0;
      return {
        usdSell: price,
        fixedCostCOP: fixed,
        validUntil: attrs.valid_until ?? null,
      };
    };

    const direct = fromBlock(res?.withdrawal);
    if (direct) return direct;

    for (const k of Object.keys(res || {})) {
      if (['payins', 'withdrawal', 'vita_sent'].includes(k)) continue;
      const section = (res as Record<string, any>)[k];
      const found = section?.withdrawal ? fromBlock(section.withdrawal) : null;
      if (found) return found;
    }
    return null;
  }

  // ─────────────── Cálculo ───────────────

  private buildCfg(): CalcConfig {
    const pos = (v: any, fb: number) => {
      const x = Number(v);
      return Number.isFinite(x) && x > 0 ? x : fb;
    };
    const nonNeg = (v: any, fb: number) => {
      const x = Number(v);
      return Number.isFinite(x) && x >= 0 ? x : fb;
    };
    return {
      trmVita: pos(this.trmVita, CALC_CONFIG_DEFAULT.trmVita),
      // 0/inválido → undefined: el motor cae a trmVita×0.995.
      trmSetup: Number(this.trmSetup) > 0 ? Number(this.trmSetup) : undefined,
      vitaFxPct: CALC_CONFIG_DEFAULT.vitaFxPct,
      vitaFijo: CALC_CONFIG_DEFAULT.vitaFijo,
      aguacateFxPct: nonNeg(
        this.aguacateFxPct,
        CALC_CONFIG_DEFAULT.aguacateFxPct,
      ),
      aguacateFijo: nonNeg(this.aguacateFijo, CALC_CONFIG_DEFAULT.aguacateFijo),
      costoFijoCOP: nonNeg(this.costoFijoCOP, CALC_CONFIG_DEFAULT.costoFijoCOP),
    };
  }

  recalcular(): void {
    const cfg = this.buildCfg();
    const monto = Number(this.montoUSD) > 0 ? Number(this.montoUSD) : 0;
    this.resultado = calcOperacion(monto, cfg);
    this.tabla = calcTabla(cfg);
  }

  /** True cuando trmSetup proviene del usd_sell real de Vita (no del ×0.995). */
  get usandoTasaRealVita(): boolean {
    return Number(this.trmSetup) > 0;
  }

  get datosListos(): boolean {
    return Number(this.trmVita) > 0 && Number(this.montoUSD) > 0;
  }

  fuenteLabel(fuente: string): string {
    switch (fuente) {
      case 'WISE':
        return 'Wise (mercado)';
      case 'INVESTING':
        return 'Investing.com (histórico)';
      case 'VITA_PUBLIC':
        return 'Vita (tasa pública)';
      case 'SETFX_PROXY':
        return 'SET-ICAP (histórico)';
      case 'WILKINSON':
        return 'Wilkinson (histórico)';
      default:
        return fuente || '—';
    }
  }
}
